import { MAX_FILE_SIZE_BYTES, MULTI_DOC_MAX_FILES_PER_APPOINTMENT } from '@nova/core';

import type { UploadDocumentResponse, UploadDocumentsParams } from '@/services/cometApiService';

export const MAX_FILES_MESSAGE = `Maximum of ${MULTI_DOC_MAX_FILES_PER_APPOINTMENT} files allowed`;

export const MAX_FILE_SIZE_MESSAGE = `Each file must be under ${
  MAX_FILE_SIZE_BYTES / 1024 / 1024
}MB`;

export const GENERIC_UPLOAD_ERROR =
  'Failed to upload one or more files, please refresh the page and try again';

type UploadFn = (params: UploadDocumentsParams) => Promise<UploadDocumentResponse[]>;

export type UploadDocumentBatchParams = {
  upload: UploadFn;
  warehouseId: string;
  appointmentIdentifier?: string;
  files: File[];
  /** Keys already attached to the field, which also count against the limit. */
  existingKeys: string[];
};

export type UploadDocumentBatchResult =
  | { status: 'uploaded'; documents: UploadDocumentResponse[] }
  | { status: 'rejected'; error: string };

function errorMessage(error: unknown): string {
  const candidate = error as { response?: { data?: { message?: unknown } }; message?: unknown };
  const apiMessage = candidate?.response?.data?.message;

  if (typeof apiMessage === 'string' && apiMessage.length > 0) {
    return apiMessage;
  }
  if (typeof candidate?.message === 'string' && candidate.message.length > 0) {
    return candidate.message;
  }

  return GENERIC_UPLOAD_ERROR;
}

/**
 * Uploads a document field's selection as one batch, all or nothing: a partial success used to
 * emit the keys that happened to make it through, leaving the check-in form with an incomplete
 * set and failing at save time instead of at upload time (PL-519).
 */
export async function uploadDocumentBatch({
  upload,
  warehouseId,
  appointmentIdentifier,
  files,
  existingKeys
}: UploadDocumentBatchParams): Promise<UploadDocumentBatchResult> {
  if (existingKeys.length + files.length > MULTI_DOC_MAX_FILES_PER_APPOINTMENT) {
    return { status: 'rejected', error: MAX_FILES_MESSAGE };
  }

  // Same ceiling the storage route enforces, so an oversized file fails here instead of after
  // the driver waited out the upload for a 413.
  if (files.some(file => file.size > MAX_FILE_SIZE_BYTES)) {
    return { status: 'rejected', error: MAX_FILE_SIZE_MESSAGE };
  }

  try {
    const documents = await upload({ warehouseId, appointmentIdentifier, files });
    return { status: 'uploaded', documents };
  } catch (error) {
    return { status: 'rejected', error: errorMessage(error) };
  }
}
